import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, BarChart3, Bell, BookOpen, Building2, CalendarDays, CheckCircle2, ChevronRight,
  CircleHelp, ClipboardCheck, ClipboardList, ClipboardPen, Clock3, Copy,
  FileBarChart, FilePlus2, FileQuestion, GraduationCap, KeyRound, Keyboard,
  LayoutDashboard, Library, LogOut, Maximize2, Menu, Moon, MoreHorizontal, Search,
  Settings, ShieldCheck, Sparkles, Sun, Target, Trophy, UserPlus, Users,
  UsersRound, X, Zap,
} from "lucide-react";
import {
  collection, doc, documentId, getDocs, query, updateDoc, where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "./firebaseConfig";
import brandMark from "./assets/images/logoNotext.png";
import studyDenHorizontal from "./assets/images/branding/Studyden_Horizontal.svg";
import { fetchStudentAssessmentResults, reconcileExpiredQuizAttempts } from "./utils/studentAssessmentApi";
import { selectLatestAttemptRecords } from "./utils/attemptResults";
import { isUntimedAssignment } from "./utils/assignmentTiming";
import { WorkTypeFilter, WorkTypeTag } from "./components/WorkType";
import {
  assignmentWorkType,
  averagesByWorkType,
  matchesWorkType,
  seriesWorkType,
  workNoun,
} from "./utils/workType";
import JoinCodeDialog from "./components/JoinCodeDialog";
import "./Dashboard.css";

export const MY_ASSESSMENTS_NAV_ITEM = Object.freeze({
  label: "My Work",
  path: "/my-assessments",
});

export const roleCanViewMyAssessments = (role) => role === "student";

const EMPTY_DATA = Object.freeze({
  quizzes: [], assignments: [], sessions: [], submissions: [], classes: [],
  students: [], memberships: [], questionMaxMarks: {}, finalResults: [],
  // Open attempts the server could not finalize (status unknown).
  unavailableAttemptIds: [],
});

export const canonicalStudentScores = (finalResults = [], assignments = []) => {
  const assignmentByQuiz = new Map(assignments.map((assignment) => [assignment.quizId, assignment]));
  return finalResults.flatMap(({ quizId, finalResult }) => {
    if (!Number.isFinite(finalResult?.percentage)) return [];
    const assignment = assignmentByQuiz.get(quizId);
    return [{
      quizId,
      percentage: finalResult.percentage,
      subject: assignment?.subjectSnapshot || "",
      title: assignment?.quizTitleSnapshot || "Assessment",
    }];
  });
};

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (Number.isFinite(value?.seconds)) return value.seconds * 1000;
  const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const snapshotItems = (snapshot) =>
  snapshot?.docs?.map((item) => ({ id: item.id, ...item.data() })) || [];

const normalizedQuestionId = (value) => String(value || "").split("/").pop();

const markedNumber = (value) => {
  if (value === null || value === undefined || value === "" || value === "Pending") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isTerminalAttempt = (item = {}) =>
  item.status === "submitted" || item.status === "expired" ||
  Boolean(item.submittedAt) || Boolean(item.completedAt);

export const attemptStatusFor = (assignment, sessions, submissions) => {
  const matchingSessions = sessions.filter(
    (item) => item.quizId === assignment.quizId && item.studentUid === assignment.studentUid
  ).sort((left, right) =>
    (Number(right.attemptNumber) || 1) - (Number(left.attemptNumber) || 1) ||
    (toMillis(right.updatedAt || right.submittedAt || right.startedAt) || 0) -
      (toMillis(left.updatedAt || left.submittedAt || left.startedAt) || 0)
  );
  const matchingSubmissions = submissions.filter(
    (item) => item.quizId === assignment.quizId &&
      (item.userId === assignment.studentUid || item.studentUid === assignment.studentUid)
  );
  if (matchingSessions.length > 0) {
    if (matchingSessions[0].status === "expired") return "expired";
    return isTerminalAttempt(matchingSessions[0]) ? "completed" : "in_progress";
  }
  if (matchingSubmissions.some(isTerminalAttempt)) return "completed";
  return "not_started";
};

// Whether the server reported this assignment's open attempt as one it could
// not finalize, so its status is unknown rather than in progress.
export const hasUnavailableAttempt = (assignment, sessions, unavailableAttemptIds = []) =>
  unavailableAttemptIds.length > 0 && sessions.some((item) => unavailableAttemptIds.includes(item.id) &&
    item.quizId === assignment.quizId && item.studentUid === assignment.studentUid);

const deadlineExpiryFor = (assignment, sessions) => sessions
  .filter((item) => item.quizId === assignment.quizId && item.studentUid === assignment.studentUid)
  .sort((left, right) => (Number(right.attemptNumber) || 1) - (Number(left.attemptNumber) || 1))[0]
  ?.expiryReason === "assignment_deadline";

export const studentAssignmentStatusLabel = (assignment, nowMs = Date.now()) => {
  if (assignment.attemptStatus === "unavailable") return "Status unavailable";
  if (assignment.attemptStatus === "expired" && assignment.deadlineExpired) return "Closed";
  const dueAtMs = toMillis(assignment.dueAt);
  if (assignment.attemptStatus !== "completed" && dueAtMs !== null && nowMs >= dueAtMs) return "Closed";
  if (assignment.attemptStatus === "expired") return "Time up";
  if (assignment.attemptStatus === "completed") return "Completed";
  if (assignment.attemptStatus === "in_progress") return "In Progress";
  return "Not Started";
};

// The explicit action a dashboard work card offers, or null when the card
// cannot be opened (status unknown, or closed before completion). Start and
// Continue name the work type: "Start homework", "Continue assessment".
export const studentAssignmentAction = (assignment, nowMs = Date.now()) => {
  if (assignment.attemptStatus === "unavailable") return null;
  if (["completed", "expired"].includes(assignment.attemptStatus)) return "View results";
  if (studentAssignmentStatusLabel(assignment, nowMs) === "Closed") return null;
  const noun = workNoun(assignmentWorkType(assignment));
  return assignment.attemptStatus === "in_progress" ? `Continue ${noun}` : `Start ${noun}`;
};

const formatDate = (value, options = {}) => {
  const millis = toMillis(value);
  if (millis === null) return "No due date";
  return new Date(millis).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: options.year === false ? undefined : "numeric",
  });
};

const relativeDue = (value) => {
  const millis = toMillis(value);
  if (millis === null) return "No due date";
  if (millis <= Date.now()) return "Closed";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(millis);
  due.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due in ${days} days`;
};

const greetingForNow = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const firstName = (name) => String(name || "").trim().split(/\s+/)[0] || "there";

export const accountDisplayName = (user, fallback = "StudyDen user") =>
  user?.username || user?.displayName || user?.email || fallback;

const initialsFor = (name) =>
  String(name || "User").trim().split(/\s+/).slice(0, 2)
    .map((part) => part[0]).join("").toUpperCase();

const resultKey = (submission) => {
  const quiz = submission.quizId || submission.quizCode || "unknown-quiz";
  const student = submission.userId || submission.studentUid || "unknown-student";
  return `${quiz}::${student}`;
};

const scoreAttempts = (submissions, questionMaxMarks, assignments = [], quizzes = []) => {
  const assignmentByAttempt = new Map(
    assignments.map((item) => [`${item.quizId}::${item.studentUid}`, item])
  );
  const quizById = new Map(quizzes.map((item) => [item.id, item]));
  const grouped = new Map();
  submissions.forEach((submission) => {
    const mark = markedNumber(submission.markAwarded);
    const questionId = normalizedQuestionId(submission.questionId);
    const maxMark = markedNumber(submission.maxMark ?? questionMaxMarks[questionId]);
    if (mark === null || maxMark === null || maxMark <= 0) return;
    const key = resultKey(submission);
    if (!grouped.has(key)) {
      const assignment = assignmentByAttempt.get(key);
      const quiz = quizById.get(submission.quizId);
      grouped.set(key, {
        key,
        studentUid: submission.userId || submission.studentUid || "",
        quizId: submission.quizId || assignment?.quizId || "",
        quizCode: submission.quizCode || quiz?.quizCode || "",
        title: assignment?.quizTitleSnapshot || quiz?.quizTitle || quiz?.title ||
          submission.quizCode || "Assessment",
        subject: assignment?.subjectSnapshot || quiz?.subject || "",
        awarded: 0, possible: 0, latestAt: 0,
      });
    }
    const group = grouped.get(key);
    group.awarded += mark;
    group.possible += maxMark;
    group.latestAt = Math.max(group.latestAt, toMillis(submission.submittedAt) || 0);
  });
  return [...grouped.values()].map((group) => ({
    ...group,
    percentage: Math.min(100, Math.max(0, (group.awarded / group.possible) * 100)),
  }));
};

const averageOf = (values) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

// Each scored series' work type, from its own attempts and assignment (see
// utils/workType). `studentUid` scopes a student's own dashboard.
const withWorkTypes = (items, sessions, assignments) => items.map((item) => {
  const owner = item.studentUid || null;
  const own = (record) => record.quizId === item.quizId && (!owner || record.studentUid === owner);
  return { ...item, workType: seriesWorkType(sessions.filter(own), assignments.find(own) || null) };
});

const percentText = (value) => value === null ? "—" : `${Math.round(value)}%`;

// Category averages shown beside an all-work average, never blended into it.
const categoryAverageDetail = (averages) =>
  `Assessments ${percentText(averages.assessment)} · Homework ${percentText(averages.homework)}`;

const countStatuses = (items) => items.reduce((result, item) => ({
  ...result, [item.attemptStatus]: result[item.attemptStatus] + 1,
}), { completed: 0, expired: 0, in_progress: 0, not_started: 0, unavailable: 0 });

const fetchQuestionMaxMarks = async (submissions) => {
  const ids = [...new Set(submissions.map((item) => normalizedQuestionId(item.questionId)).filter(Boolean))];
  if (!ids.length) return {};
  const batches = [];
  for (let index = 0; index < ids.length; index += 30) batches.push(ids.slice(index, index + 30));
  const snapshots = await Promise.all(
    batches.map((batch) =>
      getDocs(query(collection(db, "questions"), where(documentId(), "in", batch)))
    )
  );
  return Object.fromEntries(snapshots.flatMap((snapshot) =>
    snapshot.docs.map((item) => [item.id, markedNumber(item.data().maxMark) || 0])
  ));
};

const createTeacherCode = (name) => {
  const safeName = firstName(name).replace(/[^A-Za-z0-9]/g, "").toUpperCase() || "TEACHER";
  const random = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `AI-${safeName}-${random}`;
};

const teacherNavigation = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { label: "Create Test", path: "/create-quiz", icon: ClipboardPen },
  { label: "My Tests", path: "/view-quizzes", icon: ClipboardList },
  // Class management and connected students share the existing View Students screen.
  { label: "Classes & Students", path: "/view-students", icon: UsersRound },
  { label: "Organisations", path: "/organisations", icon: Building2 },
  { label: "Results & Analytics", path: "/view-quiz-responses", icon: BarChart3 },
  { label: "Generate Questions", path: "/generate-quiz-questions", icon: Sparkles },
  { label: "Question Bank", path: "/create-questions", icon: FileQuestion },
  { label: "Reports", path: "/auto-report", icon: FileBarChart },
  { label: "Settings", path: "/account-security", icon: Settings },
];

const studentNavigation = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { ...MY_ASSESSMENTS_NAV_ITEM, icon: ClipboardList },
  { label: "My Results", path: "/view-my-scores", icon: Trophy },
  { label: "My Classes", path: "/my-classes", icon: GraduationCap },
  { label: "Join Teacher", path: "/join-teacher", icon: UserPlus },
  { label: "Enter Test Code", path: "/enter-quiz", icon: KeyRound },
  { label: "Typing Practice", path: "/typing-test", icon: Keyboard },
  { label: "My Profile", path: "/account-security", icon: Settings },
];

const adminNavigation = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { label: "Manage Users", path: "/manage-users", icon: Users },
  { label: "Create Questions", path: "/create-questions", icon: FileQuestion },
  { label: "Generate Questions", path: "/generate-quiz-questions", icon: Sparkles },
  { label: "Create Test", path: "/create-quiz", icon: ClipboardPen },
  { label: "View Tests", path: "/view-quizzes", icon: ClipboardList },
  { label: "View Answers", path: "/view-quiz-responses", icon: ClipboardCheck },
  { label: "AutoReport", path: "/auto-report", icon: FileBarChart },
  { label: "Paper Submission", path: "/paper-submission", icon: FilePlus2 },
  { label: "Settings", path: "/account-security", icon: Settings },
];

const navForRole = (role) => role === "teacher" ? teacherNavigation :
  role === "admin" ? adminNavigation : studentNavigation;

const DashboardMetricCard = ({ icon: Icon, tone, value, label, detail }) => (
  <article className={`sd-metric sd-tone-${tone}`}>
    <div className="sd-icon-tile"><Icon size={21} strokeWidth={2.2} /></div>
    <div className="sd-metric-copy"><strong>{value}</strong><span>{label}</span>
      {detail && <small>{detail}</small>}</div>
  </article>
);

export const PanelHeading = ({ icon: Icon, title, subtitle, action }) => (
  <div className="sd-panel-heading">
    <div className="sd-panel-title">
      {Icon && <span className="sd-panel-icon"><Icon size={18} /></span>}
      <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
    </div>
    {action}
  </div>
);

const EmptyState = ({ children }) => <div className="sd-empty">{children}</div>;

const ProgressRing = ({ value, label }) => {
  const safeValue = Number.isFinite(value) ? Math.round(value) : 0;
  return (
    <div className="sd-progress-ring" style={{ "--progress": `${safeValue * 3.6}deg` }}>
      <div><strong>{Number.isFinite(value) ? `${safeValue}%` : "—"}</strong><span>{label}</span></div>
    </div>
  );
};

export const DashboardSidebar = ({ role, username, creditsRemaining, teacherCode, onCopyCode, copied, onLogout, open, onClose, activePath = "/dashboard", officialDashboardBrand = false }) => {
  const navigate = useNavigate();
  const items = navForRole(role);
  const activeIndex = items.findIndex(({ path }) => path === activePath);
  const [showTeacherCode, setShowTeacherCode] = useState(false);
  return (
    <>
      <aside className={`sd-sidebar${open ? " is-open" : ""}`}>
        <div className="sd-brand" onClick={() => navigate("/dashboard")} role="button" tabIndex={0} aria-label="Go to dashboard">
          <span className="sd-brand-mark" aria-hidden="true"><img src={(officialDashboardBrand || role === "admin") ? studyDenHorizontal : brandMark} alt="" /></span>
          <button className="sd-sidebar-close" type="button" onClick={(event) => {
            event.stopPropagation(); onClose();
          }} aria-label="Close menu"><X size={20} /></button>
        </div>
        <nav className="sd-sidebar-nav" aria-label={`${role} dashboard navigation`}>
          {items.map(({ label, path, icon: Icon }, index) => (
            <button key={`${label}-${index}`} type="button"
              className={index === activeIndex ? "active" : ""}
              onClick={() => { navigate(path); onClose(); }}>
              <Icon size={18} /><span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sd-sidebar-bottom">
          {role === "teacher" && (
            <div className="sd-credits-card">
              <div><Zap size={17} /><span>Credits</span></div>
              <strong>{creditsRemaining === "unlimited" ? "Unlimited" : creditsRemaining ?? "Not available"}</strong>
              <small>{creditsRemaining === "unlimited" ? "Full access" : creditsRemaining == null ? "account balance unavailable" : "credits remaining"}</small>
              {teacherCode && (
                <span className="sd-code-actions">
                  <button type="button" onClick={onCopyCode}
                    className="sd-code-button" title="Copy teacher code" aria-label={`Copy teacher code ${teacherCode}`}>
                    <Copy size={14} /> {copied ? "Copied" : teacherCode}
                  </button>
                  <button type="button" onClick={() => setShowTeacherCode(true)}
                    className="sd-code-button sd-code-show-button" aria-label="Show teacher code in large view">
                    <Maximize2 size={14} /> Show
                  </button>
                </span>
              )}
            </div>
          )}
          {showTeacherCode && teacherCode && (
            <JoinCodeDialog kind="teacher" code={teacherCode} onClose={() => setShowTeacherCode(false)} />
          )}
          {/* Support has no route or backend yet, so it is intentionally non-navigating. */}
          <button type="button" className="sd-bottom-link" disabled title="Support is coming soon">
            <CircleHelp size={18} />Help & Support
          </button>
          <button type="button" className="sd-bottom-link" onClick={onLogout}>
            <LogOut size={18} />Log Out
          </button>
          <button type="button" className="sd-profile" onClick={() => navigate("/account-security")}>
            <span className="sd-avatar">{initialsFor(username)}</span>
            <span><strong>{username || "StudyDen user"}</strong><small>{role}</small></span>
            <MoreHorizontal size={17} />
          </button>
        </div>
      </aside>
      {open && <button className="sd-sidebar-scrim" type="button" onClick={onClose}
        aria-label="Close navigation" />}
    </>
  );
};

export const DashboardHeader = ({ username, role, darkMode, onToggleTheme, onOpenMenu, search, onSearch, searchPlaceholder }) => (
  <header className="sd-topbar">
    <button className="sd-topbar-button sd-menu-button" type="button" onClick={onOpenMenu}
      aria-label="Open menu"><Menu size={20} /></button>
    <div className="sd-search-wrap"><Search size={17} />
      <input value={search} onChange={(event) => onSearch(event.target.value)}
        placeholder={searchPlaceholder || (role === "student" ? "Search work..." : "Search dashboard...")}
        aria-label="Search dashboard" />
    </div>
    <div className="sd-top-actions">
      <button className="sd-topbar-button" type="button" disabled
        title="Notifications are not connected yet" aria-label="Notifications unavailable"><Bell size={19} /></button>
      <button className="sd-topbar-button" type="button" onClick={onToggleTheme}
        aria-label={darkMode ? "Use light mode" : "Use dark mode"}>
        {darkMode ? <Sun size={19} /> : <Moon size={19} />}
      </button>
      <div className="sd-top-profile"><span className="sd-avatar">{initialsFor(username)}</span>
        <span>{role === "teacher" ? "Teacher" : role === "admin" ? "Admin" : "Student"}</span></div>
    </div>
  </header>
);

const TeacherPerformance = ({ attempts, search }) => {
  const rows = useMemo(() => {
    const grouped = new Map();
    attempts.forEach((attempt) => {
      const key = attempt.quizId || attempt.quizCode;
      if (!grouped.has(key)) grouped.set(key, { title: attempt.title, values: [] });
      grouped.get(key).values.push(attempt.percentage);
    });
    return [...grouped.values()].map((item) => ({ ...item, average: averageOf(item.values) }))
      .filter((item) => item.title.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => b.average - a.average).slice(0, 8);
  }, [attempts, search]);
  return (
    <section className="sd-panel sd-performance-panel">
      <PanelHeading icon={BarChart3} title="Assessment Performance"
        subtitle="All work: average scores across marked attempts" />
      {rows.length ? <div className="sd-bar-chart">{rows.map((row, index) => (
        <div className="sd-bar-column" key={`${row.title}-${index}`}
          title={`${row.title}: ${Math.round(row.average)}%`}>
          <span className="sd-bar-value">{Math.round(row.average)}%</span>
          <div className="sd-bar-track"><div style={{ height: `${Math.max(4, row.average)}%` }} /></div>
          <span className="sd-bar-label">{row.title}</span>
        </div>
      ))}</div> : <EmptyState>Marked assessment scores will appear here.</EmptyState>}
    </section>
  );
};

const CompletionPanel = ({ counts }) => {
  const total = counts.completed + counts.expired + counts.in_progress + counts.not_started;
  const value = total ? (counts.completed / total) * 100 : null;
  return (
    <section className="sd-panel sd-completion-panel">
      <PanelHeading icon={Target} title="Assessment Completion"
        subtitle="Across current assignments" />
      <div className="sd-completion-body"><ProgressRing value={value} label="Completed" />
        <div className="sd-legend">
          <div><i className="complete" /><span>Completed</span><strong>{counts.completed}</strong></div>
          <div><i className="progress" /><span>Time up</span><strong>{counts.expired}</strong></div>
          <div><i className="progress" /><span>In Progress</span><strong>{counts.in_progress}</strong></div>
          <div><i className="not-started" /><span>Not Started</span><strong>{counts.not_started}</strong></div>
          {counts.unavailable > 0 && (
            <div><i className="not-started" /><span>Status unavailable</span><strong>{counts.unavailable}</strong></div>
          )}
        </div>
      </div>
    </section>
  );
};

const TeacherDashboard = ({ data, search, teacherStatus, trialEndsAt, onActivate, activating, activationMessage, role }) => {
  const navigate = useNavigate();
  const assignments = useMemo(() => data.assignments.map((item) => ({
    ...item,
    attemptStatus: hasUnavailableAttempt(item, data.sessions, data.unavailableAttemptIds)
      ? "unavailable"
      : attemptStatusFor(item, data.sessions, data.submissions),
  })), [data.assignments, data.sessions, data.submissions, data.unavailableAttemptIds]);
  const attempts = useMemo(() => withWorkTypes(scoreAttempts(
    selectLatestAttemptRecords(data.submissions), data.questionMaxMarks, assignments, data.quizzes
  ), data.sessions, data.assignments), [assignments, data.assignments, data.questionMaxMarks, data.quizzes, data.sessions, data.submissions]);
  const averages = averagesByWorkType(attempts, averageOf);
  const overallAverage = averages.all;
  const completionCounts = assignments.reduce((counts, item) => ({
    ...counts, [item.attemptStatus]: counts[item.attemptStatus] + 1,
  }), { completed: 0, expired: 0, in_progress: 0, not_started: 0, unavailable: 0 });
  const assignedQuizCount = new Set(assignments.map((item) => item.quizId)).size;

  const recent = useMemo(() => {
    const grouped = new Map();
    assignments.forEach((item) => {
      const key = item.quizId || item.quizTitleSnapshot;
      const current = grouped.get(key) || {
        title: item.quizTitleSnapshot || "Assessment", count: 0, at: 0,
      };
      current.count += 1;
      current.at = Math.max(current.at, toMillis(item.assignedAt) || 0);
      grouped.set(key, current);
    });
    return [...grouped.values()]
      .filter((item) => item.title.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => b.at - a.at).slice(0, 5);
  }, [assignments, search]);

  const deadlines = useMemo(() => {
    const grouped = new Map();
    assignments.filter((item) => !["completed", "expired"].includes(item.attemptStatus) && toMillis(item.dueAt) !== null)
      .forEach((item) => {
        const key = `${item.quizId}::${toMillis(item.dueAt)}`;
        const current = grouped.get(key) || {
          title: item.quizTitleSnapshot || "Assessment", dueAt: item.dueAt, students: 0,
        };
        current.students += 1;
        grouped.set(key, current);
      });
    return [...grouped.values()]
      .filter((item) => item.title.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => toMillis(a.dueAt) - toMillis(b.dueAt)).slice(0, 5);
  }, [assignments, search]);

  const featureActions = [
    {
      label: "Create Questions",
      detail: "Design and build new questions for your tests with various formats.",
      path: "/create-questions",
      icon: FileQuestion,
      tone: "blue",
    },
    {
      label: "Generate Test Questions",
      detail: "Use AI to automatically generate test questions from your topics and content.",
      path: "/generate-quiz-questions",
      icon: Sparkles,
      tone: "purple",
    },
    {
      label: "Create Test",
      detail: "Compile questions into tests and generate access codes for students.",
      path: "/create-quiz",
      icon: ClipboardPen,
      tone: "teal",
    },
    {
      label: "View Tests",
      detail: "Browse through all available tests and their statistics.",
      path: "/view-quizzes",
      icon: ClipboardList,
      tone: "orange",
    },
    {
      label: "View Answers",
      detail: "Review student submissions and their automatically marked responses.",
      path: "/view-quiz-responses",
      icon: ClipboardCheck,
      tone: "green",
    },
    {
      label: "View Students",
      detail: "View and manage students who have joined your class using your teacher code.",
      path: "/view-students",
      icon: UsersRound,
      tone: "blue",
    },
    {
      label: "AutoReport",
      detail: "Generate personalised student reports using shared and individual data.",
      path: "/auto-report",
      icon: FileBarChart,
      tone: "purple",
    },
    {
      label: "Paper Submission",
      detail: "Upload student paper scripts and generate marked PDF reports.",
      path: "/paper-submission",
      icon: FilePlus2,
      tone: "teal",
    },
  ];
  const trialMillis = toMillis(trialEndsAt);
  const trialDays = trialMillis === null ? null : Math.ceil((trialMillis - Date.now()) / 86400000);

  return (
    <>
      {role === "teacher" && ["trial", "expired", "suspended"].includes(teacherStatus) && (
        <div className={`sd-account-notice sd-account-${teacherStatus}`}><ShieldCheck size={18} />
          <span>{teacherStatus === "suspended" ? "Your teacher account is suspended." :
            teacherStatus === "expired" || (trialDays !== null && trialDays <= 0) ?
              "Your teacher trial has ended." :
              `Teacher trial · ${trialDays ?? "—"} day${trialDays === 1 ? "" : "s"} remaining`}</span>
        </div>
      )}
      <section className="sd-metric-grid">
        <DashboardMetricCard icon={GraduationCap} tone="blue" value={data.classes.length}
          label="Classes" detail="Active classes" />
        <DashboardMetricCard icon={UsersRound} tone="teal" value={data.students.length}
          label="Students" detail="Connected students" />
        <DashboardMetricCard icon={ClipboardList} tone="purple" value={data.quizzes.length}
          label="Tests" detail={`${assignedQuizCount} assigned`} />
        <DashboardMetricCard icon={Target} tone="green"
          value={overallAverage === null ? "—" : `${Math.round(overallAverage)}%`}
          label="Average Score (all work)" detail={overallAverage === null ? "No marked results yet" :
            categoryAverageDetail(averages)} />
      </section>
      <div className="sd-analytics-grid"><TeacherPerformance attempts={attempts} search={search} />
        <CompletionPanel counts={completionCounts} /></div>
      <div className="sd-two-column">
        <section className="sd-panel">
          <PanelHeading icon={Activity} title="Recent Assessments"
            subtitle="Latest assignments from your account"
            action={<button className="sd-text-link" type="button"
              onClick={() => navigate("/view-quizzes")}>View all</button>} />
          {recent.length ? <div className="sd-compact-list">{recent.map((item, index) => (
            <button type="button" onClick={() => navigate("/view-quizzes")}
              key={`${item.title}-${index}`}>
              <span className="sd-list-icon blue"><ClipboardList size={17} /></span>
              <span><strong>{item.title}</strong><small>{item.count} student{item.count === 1 ? "" : "s"} assigned</small></span>
              <time>{item.at ? formatDate(item.at, { year: false }) : "Recently"}</time>
            </button>
          ))}</div> : <EmptyState>No assignments yet.</EmptyState>}
        </section>
        <section className="sd-panel">
          <PanelHeading icon={CalendarDays} title="Upcoming Deadlines"
            subtitle="Incomplete assigned work"
            action={<button className="sd-text-link" type="button"
              onClick={() => navigate("/view-quizzes")}>View all</button>} />
          {deadlines.length ? <div className="sd-deadline-list">{deadlines.map((item, index) => {
            const dueLabel = relativeDue(item.dueAt);
            const dueDate = new Date(toMillis(item.dueAt));
            return <button type="button" onClick={() => navigate("/view-quizzes")}
              key={`${item.title}-${index}`}>
              <span className="sd-date-block"><b>{dueDate.toLocaleString(undefined, { month: "short" }).toUpperCase()}</b><strong>{dueDate.getDate()}</strong></span>
              <span><strong>{item.title}</strong><small>{item.students} student{item.students === 1 ? "" : "s"}</small></span>
              <em className={dueLabel === "Closed" ? "overdue" : ""}>{dueLabel}</em>
            </button>;
          })}</div> : <EmptyState>No upcoming deadlines.</EmptyState>}
        </section>
      </div>
      <section className="sd-feature-section" aria-labelledby="teacher-tools-title">
        <div className="sd-feature-heading">
          <div>
            <p className="sd-eyebrow">StudyDen tools</p>
            <h2 id="teacher-tools-title">Create, assess and support your students</h2>
          </div>
          <span>Quick access to your teaching workspace</span>
        </div>
        <div className="sd-quick-grid">
          {featureActions.map(({ label, detail, path, icon: Icon, tone }) => (
            <button type="button" onClick={() => navigate(path)} key={label}
              className={`sd-quick-action sd-tone-${tone}`}>
              <span className="sd-icon-tile"><Icon size={22} /></span>
              <span><strong>{label}</strong><small>{detail}</small></span><ChevronRight size={17} />
            </button>
          ))}
        </div>
      </section>
      <section className="sd-brand-banner">
        <span className="sd-banner-icon"><GraduationCap size={34} /></span>
        <div><h2>Save time. Gain insight. Empower learning.</h2>
          <p>AI-powered assessment tools, built by teachers, for teachers.</p></div>
        <strong>StudyDen</strong>
      </section>
      {role === "admin" && <section className="sd-panel sd-admin-panel">
        <PanelHeading icon={ShieldCheck} title="Administration" subtitle="System account controls" />
        <button className="sd-primary-button" type="button" onClick={onActivate}
          disabled={activating}>{activating ? "Activating…" : "Activate Teacher Accounts"}</button>
        {activationMessage && <p className={`sd-admin-message ${activationMessage.type}`}>
          {activationMessage.text}</p>}
      </section>}
    </>
  );
};

export const StudentDashboard = ({ data, search }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("todo");
  const [workFilter, setWorkFilter] = useState("all");
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);
  const assignments = useMemo(() => data.assignments.map((item) => ({
    ...item,
    attemptStatus: hasUnavailableAttempt(item, data.sessions, data.unavailableAttemptIds)
      ? "unavailable"
      : attemptStatusFor(item, data.sessions, data.submissions),
    deadlineExpired: deadlineExpiryFor(item, data.sessions),
    workType: assignmentWorkType(item),
  })).sort((a, b) => (toMillis(a.dueAt) ?? Infinity) - (toMillis(b.dueAt) ?? Infinity)),
  [data.assignments, data.sessions, data.submissions, data.unavailableAttemptIds]);
  const attempts = useMemo(() => withWorkTypes(data.finalResults.length
    ? canonicalStudentScores(data.finalResults, assignments)
    : scoreAttempts(selectLatestAttemptRecords(data.submissions), data.questionMaxMarks, assignments),
  data.sessions, data.assignments),
  [assignments, data.assignments, data.finalResults, data.questionMaxMarks, data.sessions, data.submissions]);
  const averages = averagesByWorkType(attempts, averageOf);
  const average = averages.all;
  const allCounts = countStatuses(assignments);
  // The work-type filter applies before the status tabs, their counts and the
  // card limit, so matching work is never hidden behind other entries.
  const typedAssignments = assignments.filter((item) => matchesWorkType(item.workType, workFilter));
  const counts = countStatuses(typedAssignments);
  const filterNoun = workFilter === "homework" ? "homework" : workFilter === "assessment" ? "assessments" : "work";
  // An attempt with an unknown status needs the student's attention, so it is
  // listed under To Do with its own label rather than hidden.
  const visibleAssignments = typedAssignments.filter((item) => {
    const matchesTab = tab === "todo" ? ["not_started", "unavailable"].includes(item.attemptStatus) :
      item.attemptStatus === tab;
    const haystack = `${item.quizTitleSnapshot || ""} ${item.subjectSnapshot || ""}`.toLowerCase();
    return matchesTab && haystack.includes(search.toLowerCase());
  });
  const subjectRows = useMemo(() => {
    const subjects = new Map();
    attempts.filter((item) => item.subject).forEach((item) => {
      if (!subjects.has(item.subject)) subjects.set(item.subject, []);
      subjects.get(item.subject).push(item.percentage);
    });
    return [...subjects.entries()].map(([subject, values]) => ({
      subject, average: averageOf(values),
    })).sort((a, b) => b.average - a.average).slice(0, 5);
  }, [attempts]);

  const openAssessment = (assignment) => {
    if (assignment.attemptStatus === "unavailable") return;
    if (assignment.attemptStatus === "completed" || assignment.attemptStatus === "expired") {
      navigate(`/view-my-scores?quizId=${encodeURIComponent(assignment.quizId)}`);
      return;
    }
    if (toMillis(assignment.dueAt) !== null && nowMs >= toMillis(assignment.dueAt)) return;
    navigate(`/enter-quiz?assignmentId=${encodeURIComponent(assignment.id)}`);
  };
  const statusLabel = (assignment) => {
    return studentAssignmentStatusLabel(assignment, nowMs);
  };

  return (
    <>
      <section className="sd-metric-grid">
        <DashboardMetricCard icon={ClipboardList} tone="blue" value={assignments.length}
          label="Assigned" detail="All assigned work" />
        <DashboardMetricCard icon={Clock3} tone="orange" value={allCounts.in_progress}
          label="In Progress" detail="Continue where you left off" />
        <DashboardMetricCard icon={CheckCircle2} tone="green" value={allCounts.completed}
          label="Completed" detail="Finished work" />
        <DashboardMetricCard icon={BarChart3} tone="purple"
          value={percentText(average)} label="Average Score (all work)"
          detail={average === null ? "No marked results yet" : categoryAverageDetail(averages)} />
      </section>
      <section className="sd-panel sd-assessments-panel">
        <PanelHeading icon={ClipboardList} title="My Work"
          subtitle="Your assigned work in one place"
          action={<button className="sd-text-link" type="button"
            onClick={() => navigate("/my-assessments")}>View all</button>} />
        <WorkTypeFilter value={workFilter} onChange={setWorkFilter} className="sd-work-filter" />
        <div className="sd-tabs" role="tablist" aria-label="Work status">
          <button type="button" className={tab === "todo" ? "active" : ""}
            onClick={() => setTab("todo")}>To Do <span>{counts.not_started + counts.unavailable}</span></button>
          <button type="button" className={tab === "in_progress" ? "active" : ""}
            onClick={() => setTab("in_progress")}>In Progress <span>{counts.in_progress}</span></button>
          <button type="button" className={tab === "completed" ? "active" : ""}
            onClick={() => setTab("completed")}>Completed <span>{counts.completed}</span></button>
          <button type="button" className={tab === "expired" ? "active" : ""}
            onClick={() => setTab("expired")}>Time up <span>{counts.expired}</span></button>
        </div>
        {data.assignmentsUnavailable ? <EmptyState>We couldn't load your assigned work. Please try again shortly.</EmptyState> :
          visibleAssignments.length ? <div className="sd-assessment-list">
          {visibleAssignments.slice(0, 6).map((assignment) => {
            const label = statusLabel(assignment);
            const action = studentAssignmentAction(assignment, nowMs);
            const title = assignment.quizTitleSnapshot || "Untitled assessment";
            // The card is a pointer shortcut for its action button; keyboard and
            // screen reader users reach the same handler through the button.
            return <div key={assignment.id}
              className={`sd-assessment-card${action ? " is-actionable" : ""}`}
              onClick={action ? (event) => {
                if (!event.target.closest("button")) openAssessment(assignment);
              } : undefined}
              title={assignment.attemptStatus === "unavailable"
                ? "We could not confirm the latest status of this attempt. Your saved answers are kept."
                : label === "Closed" ? "The submission deadline has passed" : undefined}>
              <span className="sd-list-icon blue"><FileBarChart size={18} /></span>
              <span className="sd-assessment-title"><strong title={title}>{title}</strong><small><WorkTypeTag type={assignment.workType} />{assignment.subjectSnapshot || "No subject"}{isUntimedAssignment(assignment) && " · No time limit"}</small></span>
              <span className="sd-assessment-due"><CalendarDays size={14} />{label === "Closed" ? "The submission deadline has passed" : formatDate(assignment.dueAt)}</span>
              <span className={`sd-status sd-status-${label.toLowerCase().replaceAll(" ", "-")}`}>{label}</span>
              {action && <button type="button"
                className={`sd-assessment-action${action === "View results" ? " is-secondary"
                  : assignment.workType === "homework" ? " is-homework" : ""}`}
                aria-label={`${action}: ${title}`}
                onClick={() => openAssessment(assignment)}>
                {action}{action !== "View results" && <span aria-hidden="true"> →</span>}
              </button>}
            </div>;
          })}</div> : <EmptyState>{search ? `No ${filterNoun} matches your search.` :
            `No ${tab === "todo" ? "to-do" : tab === "expired" ? "time-up" : tab.replace("_", "-")} ${filterNoun}.`}</EmptyState>}
      </section>
      <div className="sd-student-insights">
        <section className="sd-panel sd-progress-panel">
          <PanelHeading icon={BarChart3} title="My Progress" subtitle="All marked work" />
          <div className="sd-progress-layout"><ProgressRing value={average} label="All work average" />
            <div className="sd-progress-stats">
              <div><i className="complete" /><span><strong>{allCounts.completed}</strong>Completed</span></div>
              <div><i className="progress" /><span><strong>{allCounts.in_progress}</strong>In Progress</span></div>
              <div><i className="not-started" /><span><strong>{allCounts.not_started}</strong>Not Started</span></div>
            </div>
          </div>
        </section>
        <section className="sd-panel sd-subject-panel">
          <PanelHeading icon={BarChart3} title="Subject Performance"
            subtitle="All work: average score by subject" />
          {subjectRows.length > 0 ? <div className="sd-subject-list">
            {subjectRows.map((row, index) => {
              const roundedAverage = Math.round(row.average);
              return <div className="sd-subject-row" key={row.subject}>
                <div className="sd-subject-meta">
                  <span title={row.subject}>{row.subject}</span>
                  <span className="sd-subject-result"><strong>{roundedAverage}%</strong>
                    {index === 0 && subjectRows.length >= 2 &&
                      <em><Trophy size={11} aria-hidden="true" />Strongest</em>}
                  </span>
                </div>
                <div className="sd-subject-track" role="progressbar"
                  aria-label={`${row.subject} average score`}
                  aria-valuemin="0" aria-valuemax="100" aria-valuenow={roundedAverage}>
                  <i style={{ width: `${row.average}%` }} className={`tone-${index % 5}`} />
                </div>
              </div>;
            })}
          </div> : <div className="sd-subject-empty">
            <span><BarChart3 size={22} aria-hidden="true" /></span>
            <p>Complete work to see your subject performance.</p>
          </div>}
        </section>
      </div>
      <div className="sd-student-bottom">
        <section className="sd-learning-card"><span className="sd-icon-tile"><BookOpen size={23} /></span>
          <div><h2>Learning Hub</h2><p>Access revision materials attached to your assigned work.</p>
            <button type="button" onClick={() => navigate("/my-assessments")}>Browse Resources</button></div>
          <Library size={60} className="sd-learning-art" />
        </section>
        <section className="sd-motivation-card"><span><Trophy size={24} /></span>
          <h2>Keep going!</h2><p>Every piece of work helps you learn and improve.</p></section>
      </div>
    </>
  );
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => ({
    username: accountDisplayName(user, ""),
    role: user?.role || "student",
    creditsRemaining: user?.role === "admin" ? "unlimited" : user?.creditsRemaining ?? null,
    teacherCode: user?.teacherCode || "",
    teacherStatus: user?.teacherStatus ?? null,
    trialEndsAt: user?.trialEndsAt ?? null,
  }));
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [dataWarning, setDataWarning] = useState("");
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activationMessage, setActivationMessage] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const loadedUid = useRef(null);
  const role = profile.role || user?.role || "student";
  const username = profile.username || accountDisplayName(user);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute("data-dark-mode", darkMode ? "true" : "false");
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    if (!user?.uid || !["teacher", "student"].includes(user.role)) return undefined;
    const refresh = () => setRefreshKey((value) => value + 1);
    const interval = window.setInterval(refresh, 30_000);
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user?.uid, user?.role]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    let cancelled = false;
    const load = async () => {
      if (loadedUid.current !== user.uid) setLoading(true);
      setDataWarning("");
      const warnings = [];
      const safeSnapshot = async (promise, label) => {
        try { return await promise; }
        catch (error) {
          console.error(`[DASHBOARD] Could not load ${label}`, error);
          warnings.push(label);
          return { docs: [] };
        }
      };
      try {
        const loadedRole = user.role || "student";
        let unavailableAttemptIds = [];
        if (loadedRole === "teacher" || loadedRole === "student") {
          const reconciliation = await reconcileExpiredQuizAttempts();
          unavailableAttemptIds = (reconciliation?.unavailable || []).map((entry) => entry.attemptId);
        }
        const loadedName = accountDisplayName(user);
        let teacherCode = user.teacherCode || "";
        if (loadedRole === "teacher" && !teacherCode) {
          teacherCode = createTeacherCode(loadedName);
          try { await updateDoc(doc(db, "users", user.uid), { teacherCode }); }
          catch (error) {
            console.error("[DASHBOARD] Could not save teacher code", error);
            teacherCode = "";
          }
        }
        if (cancelled) return;
        setProfile({
          username: loadedName, role: loadedRole,
          creditsRemaining: loadedRole === "admin" ? "unlimited" : user.creditsRemaining ?? null,
          teacherCode, teacherStatus: user.teacherStatus ?? null,
          trialEndsAt: user.trialEndsAt ?? null,
        });
        let nextData = { ...EMPTY_DATA };
        if (loadedRole === "teacher") {
          const [quizzesSnapshot, assignmentsSnapshot, sessionsSnapshot, submissionsSnapshot,
            classesSnapshot, studentsSnapshot, membershipsSnapshot] = await Promise.all([
            safeSnapshot(getDocs(query(collection(db, "quizzes"), where("creatorUid", "==", user.uid))), "quizzes"),
            safeSnapshot(getDocs(query(collection(db, "quizAssignments"), where("teacherUid", "==", user.uid))), "assignments"),
            safeSnapshot(getDocs(query(collection(db, "quizSessions"), where("teacherUid", "==", user.uid))), "quiz sessions"),
            safeSnapshot(getDocs(query(collection(db, "submissions"), where("teacherUid", "==", user.uid))), "submissions"),
            safeSnapshot(getDocs(query(collection(db, "classes"), where("teacherUid", "==", user.uid), where("active", "==", true))), "classes"),
            teacherCode ? safeSnapshot(getDocs(query(collection(db, "users"), where("role", "==", "student"), where("teacherCodes", "array-contains", teacherCode))), "students") : Promise.resolve({ docs: [] }),
            safeSnapshot(getDocs(query(collection(db, "classMemberships"), where("teacherUid", "==", user.uid))), "class memberships"),
          ]);
          const submissions = snapshotItems(submissionsSnapshot);
          let questionMaxMarks = {};
          try { questionMaxMarks = await fetchQuestionMaxMarks(submissions); }
          catch (error) {
            console.error("[DASHBOARD] Could not load score metadata", error);
            warnings.push("score metadata");
          }
          nextData = {
            quizzes: snapshotItems(quizzesSnapshot),
            assignments: snapshotItems(assignmentsSnapshot).filter((item) => !item.revokedAt),
            sessions: snapshotItems(sessionsSnapshot), submissions,
            classes: snapshotItems(classesSnapshot), students: snapshotItems(studentsSnapshot),
            memberships: snapshotItems(membershipsSnapshot).filter((item) => item.active !== false),
            questionMaxMarks, unavailableAttemptIds,
          };
        } else if (loadedRole === "student") {
          const [assignmentsSnapshot, sessionsSnapshot, studentResults, membershipsSnapshot] = await Promise.all([
            safeSnapshot(getDocs(query(collection(db, "quizAssignments"), where("studentUid", "==", user.uid))), "assignments"),
            safeSnapshot(getDocs(query(collection(db, "quizSessions"), where("studentUid", "==", user.uid))), "quiz sessions"),
            fetchStudentAssessmentResults().catch((error) => {
              console.error("[DASHBOARD] Could not load student results", error);
              warnings.push("student results");
              return { results: [], finalResults: [], attempts: [] };
            }),
            safeSnapshot(getDocs(query(collection(db, "classMemberships"), where("studentUid", "==", user.uid))), "classes"),
          ]);
          const submissions = studentResults.results;
          const questionMaxMarks = Object.fromEntries(
            submissions.map((item) => [normalizedQuestionId(item.questionId), markedNumber(item.maxMark) || 0])
          );
          const memberships = snapshotItems(membershipsSnapshot).filter((item) => item.active !== false);
          nextData = {
            ...EMPTY_DATA,
            assignments: snapshotItems(assignmentsSnapshot).filter((item) => !item.revokedAt),
            sessions: snapshotItems(sessionsSnapshot), submissions, memberships,
            classes: memberships, questionMaxMarks, finalResults: studentResults.finalResults,
            unavailableAttemptIds,
            // A failed read is not an empty list of work.
            assignmentsUnavailable: warnings.includes("assignments"),
          };
        } else {
          const [quizzesSnapshot, usersSnapshot] = await Promise.all([
            safeSnapshot(getDocs(collection(db, "quizzes")), "quizzes"),
            safeSnapshot(getDocs(collection(db, "users")), "users"),
          ]);
          nextData = { ...EMPTY_DATA, quizzes: snapshotItems(quizzesSnapshot),
            students: snapshotItems(usersSnapshot) };
        }
        if (!cancelled) {
          setData(nextData);
          loadedUid.current = user.uid;
          if (warnings.length) setDataWarning(
            "Some dashboard insights could not be loaded. Your core navigation is still available."
          );
        }
      } catch (error) {
        console.error("[DASHBOARD] Could not load profile", error);
        if (!cancelled) {
          setData(EMPTY_DATA);
          setDataWarning("We could not check the latest assessment status. Please try again.");
        }
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [user, refreshKey]);

  const handleLogout = async () => {
    try { await auth.signOut(); navigate("/"); }
    catch (error) { console.error("Error signing out", error); }
  };
  const copyTeacherCode = async () => {
    if (!profile.teacherCode) return;
    try {
      await navigator.clipboard.writeText(profile.teacherCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) { console.error("Could not copy teacher code", error); }
  };
  const activateTeachers = async () => {
    setActivating(true);
    setActivationMessage(null);
    try {
      const callable = httpsCallable(getFunctions(), "activateTeacherAccounts");
      const result = await callable();
      setActivationMessage({ type: "success",
        text: result.data?.message || "Teacher accounts activated." });
    } catch (error) {
      setActivationMessage({ type: "error",
        text: error.message || "Teacher accounts could not be activated." });
    } finally { setActivating(false); }
  };

  return (
    <div className={`sd-dashboard sd-brand-theme sd-role-${role}${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar role={role} username={username} officialDashboardBrand
        creditsRemaining={profile.creditsRemaining} teacherCode={profile.teacherCode}
        onCopyCode={copyTeacherCode} copied={copied} onLogout={handleLogout}
        open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="sd-workspace">
        <DashboardHeader username={username} role={role} darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)} search={search} onSearch={setSearch} />
        <main className="sd-main">
          <div className="sd-welcome-row"><div>
            <p className="sd-eyebrow">{role === "teacher" ? "Teacher overview" :
              role === "admin" ? "Administration overview" : "Student overview"}</p>
            <h1>{greetingForNow()}, <span className="sd-welcome-name">{firstName(username)}!</span></h1>
            <p>{role === "teacher" ? "Here’s what’s happening with your classes today." :
              role === "admin" ? "Here’s what’s happening across StudyDen today." :
                "Let’s keep up the great work."}</p>
          </div><time><CalendarDays size={16} />{new Date().toLocaleDateString(undefined, {
            weekday: "long", day: "numeric", month: "long", year: "numeric",
          })}</time></div>
          {dataWarning && <div className="sd-data-warning" role="status">{dataWarning}</div>}
          {loading ? <div className="sd-loading"><span /><p>Preparing your dashboard…</p></div> :
            role === "student" ? <StudentDashboard data={data} search={search} /> :
              <TeacherDashboard data={data} search={search}
                teacherStatus={profile.teacherStatus} trialEndsAt={profile.trialEndsAt}
                onActivate={activateTeachers} activating={activating}
                activationMessage={activationMessage} role={role} />}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
